import React from "react";
import {
  AbsoluteFill,
  Audio,
  Easing,
  Img,
  interpolate,
  OffthreadVideo,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { loadFont as loadBebas } from "@remotion/google-fonts/BebasNeue";
import { loadFont as loadOswald } from "@remotion/google-fonts/Oswald";
import { loadFont as loadAnton } from "@remotion/google-fonts/Anton";
import { loadFont as loadMontserrat } from "@remotion/google-fonts/Montserrat";
import { loadFont as loadRaleway } from "@remotion/google-fonts/Raleway";
import { loadFont as loadRoboto } from "@remotion/google-fonts/Roboto";
import { loadFont as loadPlayfair } from "@remotion/google-fonts/PlayfairDisplay";

// ── Webfont registry (mirrors the Studio dropdown) ─────────────────────────────
// Calling loadFont() registers the font with Remotion's delayRender so it is fully
// loaded before any frame is captured. Maps the UI font name → its CSS family.
const FONT_MAP: Record<string, string> = {};
const _reg = (name: string, fn: () => { fontFamily: string }) => {
  try { FONT_MAP[name] = fn().fontFamily; } catch (e) { /* keep going */ }
};
_reg("Bebas Neue", loadBebas);
_reg("Oswald", loadOswald);
_reg("Anton", loadAnton);
_reg("Montserrat", loadMontserrat);
_reg("Raleway", loadRaleway);
_reg("Roboto", loadRoboto);
_reg("Playfair Display", loadPlayfair);

const fontFamilyCss = (name?: string): string => {
  if (!name) return "'Inter', system-ui, sans-serif";
  if (FONT_MAP[name]) return `'${FONT_MAP[name]}', sans-serif`;
  return `'${name}', sans-serif`; // system fonts: Impact, Arial, Georgia, Courier New
};

// ── Text shadow / stroke helpers (identical math to the Studio preview) ─────────
const textShadowCss = (level: number): string => {
  if (level <= 0) return "";
  if (level === 1) return "1px 1px 4px rgba(0,0,0,0.7)";
  if (level === 2) return "2px 2px 12px rgba(0,0,0,0.85),0 0 4px rgba(0,0,0,0.5)";
  return "0 0 6px rgba(0,0,0,1),2px 2px 0 rgba(0,0,0,1),-1px -1px 0 rgba(0,0,0,0.8)";
};
const letterStroke = (n: number, color: string): string => {
  const k = Math.min(Math.max(Math.round(n), 1), 7);
  const s: string[] = [];
  for (let x = -k; x <= k; x++)
    for (let y = -k; y <= k; y++)
      if ((x || y) && x * x + y * y <= k * k + 0.5) s.push(`${x}px ${y}px 0 ${color}`);
  return s.join(",");
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Scene {
  id: string;
  title: string;
  body: string;
  duration: number;
  audioFile?: string;
  imageUrl?: string;   // staticFile path e.g. "image.png" or full URL
  videoUrl?: string;   // staticFile path e.g. "clip.webm" or full URL
  bg?: string;
  stat?: string;
  statLabel?: string;
  overlayOpacity?: number; // 0-1, darkens image/video bg for text legibility
  // Video clip crop (seconds within the source clip)
  clipIn?: number;   // skip N seconds from the start
  clipOut?: number;  // stop at this absolute second (0 = play to end)
  // WYSIWYG caption styling (mirrors the Studio preview)
  font?: string;
  titleFont?: string; bodyFont?: string;
  titleColor?: string; bodyColor?: string;
  titleSize?: number; bodySize?: number;       // percent, 100 = base
  titleX?: number; titleY?: number; bodyX?: number; bodyY?: number; // percent
  titleAlign?: string; bodyAlign?: string; textAlign?: string;
  titleBold?: boolean; bodyBold?: boolean; bold?: boolean;
  titleItalic?: boolean; bodyItalic?: boolean; italic?: boolean;
  outline?: number;                            // 0-3 shadow strength
  captionBg?: boolean;
  titleBorderStyle?: string; bodyBorderStyle?: string;   // none|block|rounded|stroke
  titleBorderColor?: string; bodyBorderColor?: string;
  titleBorderWidth?: number; bodyBorderWidth?: number;
}

export interface SocialReelProps {
  scenes: Scene[];
  brandName?: string;
  accentColor?: string;
  logoText?: string;
  showBrand?: boolean;
}

// ── Animation helpers ─────────────────────────────────────────────────────────

const fadeIn = (frame: number, start: number, len = 18) =>
  interpolate(frame, [start, start + len], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });

const slideUp = (frame: number, start: number, len = 18, px = 50) =>
  interpolate(frame, [start, start + len], [px, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.out(Easing.exp),
  });

const scaleIn = (frame: number, start: number, len = 14) =>
  interpolate(frame, [start, start + len], [0.85, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.out(Easing.elastic(1)),
  });

const progressBar = (frame: number, total: number) =>
  interpolate(frame, [0, total], [0, 100], { extrapolateRight: "clamp" });

// ── Caption Text (WYSIWYG — matches the Studio preview exactly) ─────────────────

const CaptionText: React.FC<{
  text: string; role: "title" | "body"; scene: Scene; frame: number;
}> = ({ text, role, scene, frame }) => {
  if (!text) return null;
  const isTitle = role === "title";

  const fontPx = (isTitle ? 48 : 24) * ((isTitle ? scene.titleSize : scene.bodySize) || 100) / 100;
  const color = (isTitle ? scene.titleColor : scene.bodyColor) || (isTitle ? "#ffffff" : "rgba(255,255,255,0.88)");
  const fontName = isTitle
    ? (scene.titleFont !== undefined ? scene.titleFont : (scene.font || ""))
    : (scene.bodyFont !== undefined ? scene.bodyFont : (scene.font || ""));
  const boldDefault = scene.bold !== false;
  const bold = isTitle
    ? (scene.titleBold !== undefined ? scene.titleBold : boldDefault)
    : (scene.bodyBold !== undefined ? scene.bodyBold : boldDefault);
  const weight = isTitle ? (bold ? 800 : 400) : (bold ? 600 : 400);
  const ital = isTitle
    ? (scene.titleItalic !== undefined ? scene.titleItalic : !!scene.italic)
    : (scene.bodyItalic !== undefined ? scene.bodyItalic : !!scene.italic);
  const align = (isTitle ? (scene.titleAlign || scene.textAlign) : (scene.bodyAlign || scene.textAlign)) || "center";
  const top = isTitle ? (scene.titleY !== undefined ? scene.titleY : 10) : (scene.bodyY !== undefined ? scene.bodyY : 42);
  const left = isTitle ? (scene.titleX !== undefined ? scene.titleX : 5) : (scene.bodyX !== undefined ? scene.bodyX : 6);

  const outline = scene.outline !== undefined ? scene.outline : 2;
  const shadowLevel = isTitle ? outline : Math.max(0, outline - 1);
  let shadow = textShadowCss(shadowLevel);

  const borderStyle = (isTitle ? scene.titleBorderStyle : scene.bodyBorderStyle) || "none";
  const borderColor = (isTitle ? scene.titleBorderColor : scene.bodyBorderColor) || "#000000";
  const borderWidth = isTitle
    ? (scene.titleBorderWidth !== undefined ? scene.titleBorderWidth : 2)
    : (scene.bodyBorderWidth !== undefined ? scene.bodyBorderWidth : 2);
  if (borderStyle === "stroke") {
    const st = letterStroke(borderWidth, borderColor);
    shadow = shadow ? shadow + "," + st : st;
  }

  const fadeStart = isTitle ? (scene.stat ? 20 : 10) : (scene.stat ? 32 : 22);
  const opacity = fadeIn(frame, fadeStart);

  let inner: React.ReactNode = text;
  if (borderStyle === "block" || borderStyle === "rounded") {
    inner = (
      <span style={{
        display: "inline-block", background: borderColor,
        borderRadius: borderStyle === "rounded" ? 8 + borderWidth * 2 : 0,
        padding: `${borderWidth * 2}px ${borderWidth * 3}px`,
      }}>{text}</span>
    );
  }

  const style: React.CSSProperties = {
    position: "absolute", top: `${top}%`, left: `${left}%`,
    width: isTitle ? "90%" : "88%",
    textAlign: align as React.CSSProperties["textAlign"],
    color, fontSize: fontPx, fontWeight: weight,
    fontStyle: ital ? "italic" : "normal",
    fontFamily: fontFamilyCss(fontName),
    lineHeight: isTitle ? 1.15 : 1.4,
    opacity,
  };
  if (shadow) style.textShadow = shadow;
  if (outline === 3) style.WebkitTextStroke = "1.5px rgba(0,0,0,0.9)";
  if (scene.captionBg) {
    style.background = "rgba(0,0,0,0.55)";
    style.display = "inline-block";
    style.padding = isTitle ? "3px 10px" : "2px 8px";
    style.borderRadius = 4;
  }

  return <div style={style}>{inner}</div>;
};

// ── Scene Card ────────────────────────────────────────────────────────────────

const SceneCard: React.FC<{
  scene: Scene; accent: string; brand: string;
  sceneIndex: number; totalScenes: number;
  showBrand?: boolean;
}> = ({ scene, accent, brand, sceneIndex, totalScenes, showBrand = false }) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  const isVertical = height > width;

  const bg = scene.bg || `linear-gradient(160deg, #020617 0%, #0f172a 50%, #020c1f 100%)`;
  const progress = progressBar(frame, scene.duration);
  const hasMedia = !!(scene.imageUrl || scene.videoUrl);
  const overlay = scene.overlayOpacity ?? (hasMedia ? 0.55 : 0);

  // Resolve media src: if it's a full URL use as-is, otherwise treat as staticFile
  const mediaSrc = (src: string) =>
    src.startsWith('http') || src.startsWith('/media/') || src.startsWith('data:') ? src : staticFile(src);

  return (
    <AbsoluteFill style={{ background: hasMedia ? '#000' : bg, fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>

      {/* Background video — use OffthreadVideo so frames are fully loaded before render.
          clipIn/clipOut crop each end of the source clip independently (seconds → frames). */}
      {scene.videoUrl && (
        <OffthreadVideo
          src={mediaSrc(scene.videoUrl)}
          trimBefore={scene.clipIn && scene.clipIn > 0 ? Math.round(scene.clipIn * fps) : undefined}
          trimAfter={scene.clipOut && scene.clipOut > 0 ? Math.round(scene.clipOut * fps) : undefined}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Background image — use Img so it's preloaded before render */}
      {scene.imageUrl && !scene.videoUrl && (
        <Img
          src={mediaSrc(scene.imageUrl)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Dark overlay for text legibility over media */}
      {overlay > 0 && (
        <div style={{ position: "absolute", inset: 0, background: `rgba(2,6,23,${overlay})` }} />
      )}

      {/* Subtle grid overlay (skip when media bg to keep image clean) */}
      {!hasMedia && <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: `linear-gradient(${accent}44 1px, transparent 1px), linear-gradient(90deg, ${accent}44 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />}

      {/* Top glow */}
      <div style={{
        position: "absolute", top: -120, left: "50%", transform: "translateX(-50%)",
        width: 600, height: 400,
        background: `radial-gradient(ellipse, ${accent}18 0%, transparent 70%)`,
      }} />

      {/* Top accent bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent,
        boxShadow: `0 0 20px ${accent}88`,
      }} />

      {/* Brand — only shown when showBrand is true */}
      {showBrand && (
      <div style={{
        position: "absolute", top: isVertical ? 60 : 40, left: 60, right: 60,
        opacity: fadeIn(frame, 5),
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          background: accent, color: "#020617", padding: "6px 16px",
          borderRadius: 6, fontSize: isVertical ? 28 : 20,
          fontWeight: 900, letterSpacing: 1, textTransform: "uppercase",
        }}>
          {brand}
        </div>
        <div style={{
          fontSize: isVertical ? 22 : 16, color: `${accent}99`,
          fontWeight: 600, letterSpacing: 3, textTransform: "uppercase",
        }}>
          {`${sceneIndex + 1} / ${totalScenes}`}
        </div>
      </div>
      )}

      {/* Stat callout (optional) — kept centered for the template reels */}
      {scene.stat && (
        <AbsoluteFill style={{
          justifyContent: "center", alignItems: "flex-start",
          paddingLeft: 60, paddingRight: 60, paddingTop: isVertical ? 120 : 80,
        }}>
          <div style={{
            opacity: fadeIn(frame, 8),
            transform: `scale(${scaleIn(frame, 8)})`,
            background: `${accent}14`,
            border: `2px solid ${accent}44`,
            borderRadius: 16,
            padding: "20px 32px",
            display: "inline-flex", flexDirection: "column", alignItems: "flex-start",
          }}>
            <div style={{ fontSize: isVertical ? 100 : 72, fontWeight: 900, color: accent, lineHeight: 1 }}>
              {scene.stat}
            </div>
            {scene.statLabel && (
              <div style={{ fontSize: isVertical ? 30 : 22, color: "#64748b", marginTop: 8, fontWeight: 500 }}>
                {scene.statLabel}
              </div>
            )}
          </div>
        </AbsoluteFill>
      )}

      {/* Header + Sub Header — WYSIWYG: absolute %-positioned, styled per scene */}
      <CaptionText text={scene.title} role="title" scene={scene} frame={frame} />
      <CaptionText text={scene.body} role="body" scene={scene} frame={frame} />

      {/* Bottom progress bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 5,
        background: "#1e3a5f",
      }}>
        <div style={{
          height: "100%", width: `${progress}%`,
          background: `linear-gradient(90deg, ${accent}, ${accent}cc)`,
          boxShadow: `0 0 10px ${accent}88`,
          transition: "none",
        }} />
      </div>

      {/* Audio */}
      {scene.audioFile && <Audio src={scene.audioFile.startsWith("http")||scene.audioFile.startsWith("/") ? scene.audioFile : staticFile(scene.audioFile)} />}
    </AbsoluteFill>
  );
};

// ── Social Reel (9:16) ────────────────────────────────────────────────────────

const DEFAULT_SCENES: Scene[] = [
  { id: "s1", title: "Your Title Here", body: "Add your content here", duration: 90 },
];

export const SocialReel: React.FC<SocialReelProps> = ({
  scenes = DEFAULT_SCENES,
  brandName = "ZERO SOUL AI",
  accentColor = "#38bdf8",
  showBrand = false,
}) => {
  let offset = 0;
  return (
    <AbsoluteFill>
      {scenes.map((scene, i) => {
        const start = offset;
        offset += scene.duration;
        return (
          <Sequence key={scene.id} from={start} durationInFrames={scene.duration}>
            <SceneCard scene={scene} accent={accentColor} brand={brandName}
              sceneIndex={i} totalScenes={scenes.length} showBrand={showBrand} />
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

// ── PowerPicks Reel ───────────────────────────────────────────────────────────

const PP_DEFAULT_SCENES: Scene[] = [
  {
    id: "hook", title: "73% of bettors lose.", body: "Not because of bad luck.", duration: 75,
    stat: "73%", statLabel: "average loss rate",
    bg: "linear-gradient(160deg, #020617 0%, #0a1628 60%, #020617 100%)",
  },
  {
    id: "value", title: "The edge is real.", body: "Sharp money alerts. Live line movements. Proven win rates.", duration: 100,
    bg: "linear-gradient(160deg, #020617 0%, #091a2e 60%, #020617 100%)",
  },
  {
    id: "cta", title: "Join PowerPicks AI.", body: "7 tools. Real-time intelligence. $33/week.", duration: 95,
    stat: "$33", statLabel: "per week",
    bg: "linear-gradient(160deg, #020617 0%, #0f172a 60%, #020617 100%)",
  },
];

export const PowerPicksReel: React.FC<Partial<SocialReelProps>> = ({
  scenes = PP_DEFAULT_SCENES,
  brandName = "POWERPICKS",
  accentColor = "#38bdf8",
}) => {
  return <SocialReel scenes={scenes} brandName={brandName} accentColor={accentColor} />;
};

// ── Square Post (1:1) ─────────────────────────────────────────────────────────

export interface SquarePostProps {
  title: string;
  subtitle?: string;
  tag?: string;
  accentColor?: string;
  brandName?: string;
  audioFile?: string;
  stat?: string;
}

export const SquarePost: React.FC<SquarePostProps> = ({
  title = "Post Title", subtitle = "", tag = "#trending",
  accentColor = "#38bdf8", brandName = "ZERO SOUL AI",
  audioFile, stat,
}) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  return (
    <AbsoluteFill style={{
      background: "linear-gradient(135deg, #020617 0%, #0f172a 60%, #020617 100%)",
      fontFamily: "'Inter', system-ui, sans-serif",
      justifyContent: "center", alignItems: "center", flexDirection: "column",
      padding: 80, overflow: "hidden",
    }}>
      {/* Grid */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: `linear-gradient(${accentColor}44 1px, transparent 1px), linear-gradient(90deg, ${accentColor}44 1px, transparent 1px)`,
        backgroundSize: "54px 54px",
      }} />
      {/* Top bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accentColor, boxShadow: `0 0 20px ${accentColor}88` }} />
      {/* Brand */}
      <div style={{ opacity: fadeIn(frame, 5), fontSize: 24, fontWeight: 900, color: accentColor, marginBottom: 28, letterSpacing: 3, textTransform: "uppercase" }}>
        {brandName}
      </div>
      {/* Stat */}
      {stat && (
        <div style={{ opacity: fadeIn(frame, 5), transform: `scale(${scaleIn(frame, 5)})`, fontSize: 110, fontWeight: 900, color: accentColor, lineHeight: 1, marginBottom: 16 }}>
          {stat}
        </div>
      )}
      {/* Title */}
      <div style={{ opacity: fadeIn(frame, 10), transform: `translateY(${slideUp(frame, 10)}px)`, fontSize: 72, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.1, marginBottom: 28 }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ opacity: fadeIn(frame, 22), transform: `translateY(${slideUp(frame, 22)}px)`, fontSize: 36, color: "#64748b", textAlign: "center", lineHeight: 1.5, marginBottom: 28 }}>
          {subtitle}
        </div>
      )}
      <div style={{ opacity: fadeIn(frame, 35), fontSize: 28, color: accentColor, fontWeight: 700 }}>{tag}</div>
      {/* Progress */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, durationInFrames)}%`, background: accentColor }} />
      </div>
      {audioFile && <Audio src={audioFile.startsWith("http")||audioFile.startsWith("/") ? audioFile : staticFile(audioFile)} />}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
